import React, { useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { useHistory } from "react-router-dom";
import {
  ArrowBack,
  ArrowForward,
  Autorenew,
  CancelOutlined,
  Check,
  CheckCircleOutline,
  Close,
  CurrencyRupee,
  FileDownloadOutlined,
  History,
  OpenInNew,
  PriorityHighOutlined,
} from "@mui/icons-material";
import { StatCard, designColors, designShell, designType, designWeights } from "services/constants/designTokens";
import { GlobalBreadcrumb, globalTextFieldSx } from "components/DesignSystem/sharedUiPatterns";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { Field } from "../Shared/formUi";
import { SelectField, TextField } from "../Shared/fieldInputs";
import { defaultMonths, monthsBetween, periodText } from "./bonusCalc";
import { addBonusRunToPayroll, calculateBonusRun, cycleDates, cycleLabel, draftIsStale } from "./bonusSampleApi";
import { STATUTORY_NOTICE } from "pages/PayRoll/Configuration/PayInputConfiguration/bonusTypesData";
import { PAY_INPUT_CONFIG_BASE_PATH } from "pages/PayRoll/Configuration/PayInputConfiguration";
import { EmployeeMonthsPanel, MonthwiseTable, SummaryTable, ViewToggle } from "./BonusRunPanels";
import {
  DAYS_LABEL,
  DetailRows,
  MONTH_OPTIONS,
  SectionCard,
  downloadRunWorkbook,
  forPeriod,
  methodShort,
  rupees,
  wagesLabel,
} from "./bonusUi";

const STEPS = ["Set up", "Check results", "Add to payroll"];
const ROUNDING_TEXT = { NEAREST: "Nearest rupee", UP: "Round up", DOWN: "Round down", NONE: "No rounding (2 decimals)" };

// Inline "1 Set up —— 2 Check results —— 3 Add to payroll" (Figure A4).
const Stepper = ({ step }) => (
  <Mui.Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5, flexWrap: "wrap", rowGap: 1 }}>
    {STEPS.map((label, index) => {
      const done = index < step;
      const active = index === step;
      return (
        <React.Fragment key={label}>
          {index > 0 ? <Mui.Box sx={{ width: 56, height: 2, backgroundColor: done || active ? designColors.teal : designColors.slate200 }} /> : null}
          <Mui.Stack direction="row" spacing={1} alignItems="center">
            <Mui.Box sx={{
              width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 13, fontWeight: designWeights.semibold,
              backgroundColor: done || active ? designColors.tealDark : designColors.slate100,
              color: done || active ? designColors.white : designColors.slate600,
            }}>
              {done ? <Check sx={{ fontSize: 16 }} /> : index + 1}
            </Mui.Box>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: active ? designWeights.semibold : designWeights.medium, color: active || done ? designColors.slate900 : designColors.slate600 }}>
              {label}
            </Mui.Typography>
          </Mui.Stack>
        </React.Fragment>
      );
    })}
  </Mui.Stack>
);

const radioSx = { color: designColors.slate400, "&.Mui-checked": { color: designColors.tealDark } };

const Choice = ({ value, label, helper, children }) => (
  <Mui.Box>
    <Mui.FormControlLabel
      value={value}
      control={<Mui.Radio size="small" sx={radioSx} />}
      label={<Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }}>{label}</Mui.Typography>}
      sx={{ m: 0 }}
    />
    {helper ? <Mui.Typography sx={{ ...designType.captionText, ml: 3.75, mt: -0.25 }}>{helper}</Mui.Typography> : null}
    {children ? <Mui.Box sx={{ ml: 3.75, mt: 1 }}>{children}</Mui.Box> : null}
  </Mui.Box>
);

const MultiSelect = ({ value, options, onChange, placeholder }) => (
  <Mui.Autocomplete
    multiple
    size="small"
    disableCloseOnSelect
    options={options}
    value={options.filter((option) => value.includes(option.value))}
    getOptionLabel={(option) => option.label}
    isOptionEqualToValue={(a, b) => a.value === b.value}
    onChange={(event, picked) => onChange(picked.map((option) => option.value))}
    renderInput={(params) => <Mui.TextField {...params} placeholder={value.length ? "" : placeholder} sx={globalTextFieldSx} />}
  />
);

const cardDefs = (totals) => [
  { key: "eligible", title: "Eligible", value: totals.eligible, icon: CheckCircleOutline, accent: "#12B76A", tint: "#E7F8EF" },
  { key: "excluded", title: "Excluded", value: totals.excluded, icon: CancelOutlined, accent: "#DC2626", tint: "#FEF2F2" },
  { key: "check", title: "Need checking", value: totals.check, icon: PriorityHighOutlined, accent: "#F0954A", tint: "#FFF4E5" },
  { key: "due", title: "Bonus due", value: rupees(totals.due), icon: CurrencyRupee, accent: "#2878FF", tint: "#EAF2FF" },
  { key: "earlier", title: "Earlier bonus", value: rupees(totals.already_paid), icon: History, accent: "#E0457B", tint: "#FDECF2" },
  { key: "pay", title: "Paying now", value: rupees(totals.pay_now), icon: CurrencyRupee, accent: "#067647", tint: "#E7F8EF" },
];

// Rule used (read-only, Figure A4 right). Shows Days counted (spec B8).
const ruleRows = (type, rounding) => {
  if (!type) return [];
  const daysText = type.min_days != null && type.min_days !== "" ? `${Number(type.min_days)} ${DAYS_LABEL[type.days_basis]?.toLowerCase() || "days"}` : "None";
  return [
    { label: "Bonus type version", value: `v${type.version} (latest)` },
    { label: "Method", value: methodShort(type) },
    type.method === "percent_of_wages" ? { label: "Bonus rate", value: `${Number(type.rate)}%` } : null,
    type.method === "per_day" ? { label: "Amount per day", value: rupees(type.per_day_amount) } : null,
    type.method === "fixed_or_days" && type.calculation_basis === "fixed_amount" ? { label: "Fixed amount", value: `${rupees(type.fixed_amount)} once per employee` } : null,
    type.method === "fixed_or_days" && type.calculation_basis === "days_wages" ? { label: "Days' wages", value: `${Number(type.days)} days ÷ ${type.day_divisor === "actual" ? "actual days" : type.day_divisor} of the To month` } : null,
    type.method === "formula" ? { label: "Formula", value: type.formula } : null,
    type.method === "percent_of_wages" || type.calculation_basis === "days_wages" ? { label: "Wages used", value: `${wagesLabel(type)} earned ${type.method === "percent_of_wages" ? "each month" : "in the To month"}` } : null,
    type.method === "percent_of_wages" ? { label: "Wage ceiling per month", value: type.ceiling ? rupees(type.ceiling) : "None" } : null,
    type.method === "percent_of_wages" ? { label: "Pay only if monthly wage ≤", value: type.wage_limit ? rupees(type.wage_limit) : "No limit" } : null,
    type.method === "percent_of_wages" || type.method === "per_day" || type.method === "formula" ? { label: "Minimum days", value: daysText } : null,
    type.method === "per_day" || (type.min_days != null && type.min_days !== "") ? { label: "Days counted", value: DAYS_LABEL[type.days_basis] || "—" } : null,
    { label: "Rounding", value: `${ROUNDING_TEXT[rounding] || rounding}, on the total` },
    { label: "Part payments", value: type.allow_part ? "Allowed" : "Not allowed" },
  ];
};

// Payroll > Pay Inputs > Bonus > Calculate Bonus: Set up -> Check results
// -> Add to payroll (spec A5, B8). Nothing goes to payroll until step 3.
export const CalculateBonus = ({ companyId, types, rounding, employees, cycles, onClose, onAdded, onOpenRun }) => {
  const history = useHistory();
  const calcTypes = types.filter((type) => type.status !== "Inactive" && type.method !== "enter_amount");
  const openCycles = cycles.filter((cycle) => !cycle.frozen);
  const [step, setStep] = useState(0);
  const [setup, setSetup] = useState(() => {
    const first = calcTypes[0];
    const months = defaultMonths(first?.default_period || "month");
    return {
      typeId: first?.id ?? "",
      from: months.from,
      to: months.to,
      payMode: "full_balance",
      partPercent: "25",
      who: "all",
      sites: [],
      departments: [],
      employeeIds: [],
      cycleId: openCycles[0]?.id ?? "",
    };
  });
  const [submitted, setSubmitted] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState("summary");
  const [show, setShow] = useState("all");
  const [site, setSite] = useState("");
  const [search, setSearch] = useState("");
  const [employeeId, setEmployeeId] = useState(null);
  const [runName, setRunName] = useState("");

  const type = types.find((t) => t.id === Number(setup.typeId));
  const draftType = draft ? types.find((t) => t.id === draft.bonus_type_id) : null;
  const stale = draft ? draftIsStale(draft, draftType) : false;
  const set = (key) => (value) => setSetup((prev) => ({ ...prev, [key]: value }));

  const sites = [...new Set(employees.map((e) => e.site).filter(Boolean))].map((s) => ({ value: s, label: s }));
  const departments = [...new Set(employees.map((e) => e.department).filter(Boolean))].map((d) => ({ value: d, label: d }));
  const selectedEmployees = employees.filter((e) => {
    if (setup.who === "list") return setup.employeeIds.includes(e.id);
    if (setup.who === "filter") {
      return (!setup.sites.length || setup.sites.includes(e.site)) && (!setup.departments.length || setup.departments.includes(e.department));
    }
    return true;
  });

  const months = monthsBetween(setup.from, setup.to);
  const errors = {};
  if (!type) errors.typeId = "Select the bonus type.";
  if (!setup.from) errors.from = "Select From month.";
  if (!setup.to) errors.to = "Select To month.";
  else if (setup.from && setup.to < setup.from) errors.to = "To month must be the same as or after From month.";
  else if (months.length > 24) errors.to = "A run can cover at most 24 months.";
  if (setup.payMode === "part") {
    const p = Number(setup.partPercent);
    if (!(p > 0 && p <= 100)) errors.partPercent = "Part % must be more than 0 and at most 100.";
  }
  if (!setup.cycleId) errors.cycleId = "Select the payroll cycle.";
  if (!selectedEmployees.length) errors.who = "No employees selected.";
  const shown = submitted ? errors : {};

  const pickType = (id) => {
    const next = types.find((t) => t.id === Number(id));
    const months2 = next?.default_period && next.default_period !== "custom" ? defaultMonths(next.default_period) : null;
    setSetup((prev) => ({
      ...prev,
      typeId: id === "" ? "" : Number(id),
      ...(months2 || {}),
      payMode: next?.allow_part ? prev.payMode : "full_balance",
    }));
  };

  const calculate = async () => {
    setSubmitted(true);
    if (Object.keys(errors).length || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await calculateBonusRun({
        companyId, type, from: setup.from, to: setup.to, payMode: setup.payMode, partPercent: setup.partPercent,
        cycleId: setup.cycleId, employeeIds: selectedEmployees.map((e) => e.id), rounding,
      });
      setDraft(result);
      setStep(1);
    } catch (err) {
      setError({ message: err.message, runId: err.meta?.run_id });
    } finally {
      setBusy(false);
    }
  };

  const recalculate = async () => {
    if (busy || !draftType) return;
    setBusy(true);
    setError(null);
    try {
      const result = await calculateBonusRun({
        companyId, type: draftType, from: draft.from_month, to: draft.to_month, payMode: draft.pay_mode, partPercent: draft.part_percent,
        cycleId: draft.payroll_cycle_id, employeeIds: draft.employee_ids, rounding,
      });
      setDraft(result);
      setStep(1);
    } catch (err) {
      setError({ message: err.message, runId: err.meta?.run_id });
    } finally {
      setBusy(false);
    }
  };

  const addToPayroll = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const outcome = await addBonusRunToPayroll({ companyId, draft, name: runName.trim(), currentType: draftType });
      onAdded(outcome);
    } catch (err) {
      setError({ message: err.message, recalc: err.code === "LEDGER_CHANGED" || err.code === "STALE" });
      setBusy(false);
    }
  };

  const rows = useMemo(() => {
    if (!draft) return [];
    const text = search.trim().toLowerCase();
    return draft.rows.filter((row) => (show === "all" || row.result === show)
      && (!site || row.site === site)
      && (!text || `${row.employee_name} ${row.employee_code}`.toLowerCase().includes(text)));
  }, [draft, show, site, search]);
  const allEligible = draft ? draft.rows.filter((row) => row.result === "eligible") : [];
  const payable = allEligible.filter((row) => row.pay_now > 0);
  const nothingToPay = allEligible.length - payable.length;
  const selectedRow = draft?.rows.find((row) => row.employee_id === employeeId);
  const cycle = draft ? cycles.find((c) => c.id === draft.payroll_cycle_id) : null;
  const leftOut = draft ? draft.totals.excluded + draft.totals.check : 0;
  const earlierPaid = payable.reduce((sum, row) => sum + row.paid_in_salary, 0);
  const earlierWaiting = payable.reduce((sum, row) => sum + row.waiting_in_payroll, 0);

  const errorBanner = error ? (
    <Mui.Alert
      severity="error"
      sx={{ mb: 2 }}
      action={error.runId ? (
        <Mui.Button color="inherit" size="small" onClick={() => onOpenRun(error.runId)}>Open {error.runId}</Mui.Button>
      ) : error.recalc ? (
        <Mui.Button color="inherit" size="small" onClick={recalculate}>Recalculate</Mui.Button>
      ) : null}
    >
      {error.message}
    </Mui.Alert>
  ) : null;

  return (
    <Mui.Box sx={{ ...designShell.pageSurface, px: 2, pt: 2, pb: 4 }}>
      <Mui.Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Mui.Box>
          <GlobalBreadcrumb trail={["Payroll", "Pay Inputs", { label: "Bonus", onClick: onClose }, "Calculate Bonus"]} />
          <Mui.Typography component="h1" sx={designType.pageTitle}>Calculate Bonus</Mui.Typography>
          <Mui.Typography sx={designType.pageSubtitle}>Work out bonus for many employees at once. Nothing is added to payroll until the last step.</Mui.Typography>
        </Mui.Box>
        <Mui.Button startIcon={<Close />} onClick={onClose} sx={{ textTransform: "none", fontWeight: designWeights.semibold, color: designColors.tealDark }}>Close</Mui.Button>
      </Mui.Stack>
      <Stepper step={step} />
      {errorBanner}

      {step === 0 ? (
        <>
          <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.5fr) minmax(0, 1fr)" }, gap: 2, alignItems: "start" }}>
            <Mui.Stack spacing={2}>
              <SectionCard title="1. Which bonus, for which months?">
                <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                  <Field label="Bonus type *" error={shown.typeId} wide>
                    <SelectField
                      value={setup.typeId}
                      onChange={pickType}
                      options={calcTypes.map((t) => ({ value: t.id, label: t.name }))}
                      placeholder={calcTypes.length ? "Select bonus type" : "No active bonus types to calculate"}
                      error={shown.typeId}
                    />
                  </Field>
                  <Field label="From month *" error={shown.from}>
                    <SelectField value={setup.from} onChange={set("from")} options={MONTH_OPTIONS} placeholder="Month" error={shown.from} />
                  </Field>
                  <Field label="To month *" error={shown.to}>
                    <SelectField value={setup.to} onChange={set("to")} options={MONTH_OPTIONS} placeholder="Month" error={shown.to} />
                  </Field>
                </Mui.Box>
                <Mui.Typography sx={{ ...designType.captionText, mt: 1 }}>
                  {months.length ? `${months.length} month${months.length === 1 ? "" : "s"} · ${forPeriod(setup.from, setup.to)}. ` : ""}Default months come from the bonus type; you can change them.
                </Mui.Typography>
                {!calcTypes.length ? (
                  <Mui.Box sx={{ mt: 1.5 }}>
                    <RequestInfoBanner tone="warning" body="No Active bonus type can be calculated. Starter types start Inactive — check their rules and activate them in Bonus settings. Enter amount types are added with + Add Bonus or Import." />
                  </Mui.Box>
                ) : null}
              </SectionCard>

              <SectionCard title="2. How much to pay now?">
                <Mui.RadioGroup value={setup.payMode} onChange={(event) => set("payMode")(event.target.value)}>
                  <Choice value="full_balance" label="Full bonus due (or the balance)" helper="Pays everything due for these months, minus earlier bonus" />
                  {type?.allow_part ? (
                    <Choice value="part" label="Part of it" helper="A % of the full bonus due, never more than the balance — e.g. 25% at Pongal, 25% at Diwali">
                      {setup.payMode === "part" ? (
                        <Mui.Box sx={{ maxWidth: 200 }}>
                          <Field label="Part % *" error={shown.partPercent}>
                            <TextField type="number" value={setup.partPercent} onChange={set("partPercent")} placeholder="e.g. 25" error={shown.partPercent} />
                          </Field>
                        </Mui.Box>
                      ) : null}
                    </Choice>
                  ) : null}
                </Mui.RadioGroup>
              </SectionCard>

              <SectionCard title="3. Who?">
                <Mui.RadioGroup value={setup.who} onChange={(event) => set("who")(event.target.value)}>
                  <Choice value="all" label="Everyone" />
                  <Choice value="filter" label="Filter by site / department">
                    {setup.who === "filter" ? (
                      <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                        <MultiSelect value={setup.sites} options={sites} onChange={set("sites")} placeholder="All sites" />
                        <MultiSelect value={setup.departments} options={departments} onChange={set("departments")} placeholder="All departments" />
                      </Mui.Box>
                    ) : null}
                  </Choice>
                  <Choice value="list" label="Choose employees">
                    {setup.who === "list" ? (
                      <MultiSelect value={setup.employeeIds} options={employees.map((e) => ({ value: e.id, label: `${e.name} (${e.code})` }))} onChange={set("employeeIds")} placeholder="Select employees" />
                    ) : null}
                  </Choice>
                </Mui.RadioGroup>
                <Mui.Typography sx={{ ...designType.captionText, mt: 1, color: shown.who ? "#DC2626" : undefined }}>
                  {selectedEmployees.length} employee{selectedEmployees.length === 1 ? "" : "s"} selected. Eligibility rules are checked in the next step.
                </Mui.Typography>
              </SectionCard>
            </Mui.Stack>

            <Mui.Stack spacing={2}>
              <SectionCard title="4. Pay with">
                <Field label="Payroll cycle *" error={shown.cycleId} wide>
                  <SelectField value={setup.cycleId} onChange={(value) => set("cycleId")(value === "" ? "" : Number(value))} options={openCycles.map((c) => ({ value: c.id, label: cycleLabel(c) }))} placeholder="Select payroll cycle" error={shown.cycleId} />
                </Field>
                <Mui.Typography sx={{ ...designType.captionText, mt: 1 }}>Only open (not frozen) cycles are shown.</Mui.Typography>
              </SectionCard>
              {type?.is_statutory ? <RequestInfoBanner tone="warning" title="Statutory bonus" body={STATUTORY_NOTICE} /> : null}
              {type ? (
                <SectionCard
                  title="Rule used"
                  action={
                    <Mui.Button
                      size="small"
                      endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                      onClick={() => history.push(`${PAY_INPUT_CONFIG_BASE_PATH}/bonus`)}
                      sx={{ textTransform: "none", fontWeight: designWeights.semibold, color: designColors.tealDark }}
                    >
                      Change in settings
                    </Mui.Button>
                  }
                >
                  <DetailRows rows={ruleRows(type, rounding)} />
                </SectionCard>
              ) : null}
            </Mui.Stack>
          </Mui.Box>
          <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 2 }}>
            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
            <PrimaryButton endIcon={<ArrowForward />} disabled={busy} onClick={calculate}>{busy ? "Calculating..." : "Next: Check results"}</PrimaryButton>
          </Mui.Stack>
        </>
      ) : null}

      {step === 1 && draft ? (
        <>
          {stale ? (
            <Mui.Box sx={{ mb: 2 }}>
              <RequestInfoBanner
                tone="warning"
                title="Data changed since this was calculated"
                body="The bonus type changed or this calculation is older than 7 days. Recalculate before adding to payroll."
                action={<PrimaryButton startIcon={<Autorenew />} disabled={busy} onClick={recalculate}>Recalculate</PrimaryButton>}
              />
            </Mui.Box>
          ) : null}
          <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))", xl: "repeat(6, minmax(0, 1fr))" }, gap: 1.5, mb: 2 }}>
            {cardDefs(draft.totals).map((card) => (
              <StatCard key={card.key} icon={card.icon} title={card.title} value={card.value} accent={card.accent} tint={card.tint} />
            ))}
          </Mui.Box>
          <Mui.Box sx={{ ...designShell.sectionCard, p: 2 }}>
            <Mui.Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} sx={{ mb: 1.5 }}>
              <ViewToggle value={view} onChange={setView} />
              <Mui.Box sx={{ width: 170 }}>
                <SelectField value={show} onChange={(value) => setShow(value || "all")} options={[{ value: "all", label: "Show: All" }, { value: "eligible", label: "Show: Eligible" }, { value: "excluded", label: "Show: Excluded" }, { value: "check", label: "Show: Need checking" }]} />
              </Mui.Box>
              <Mui.Box sx={{ width: 190 }}>
                <SelectField value={site} onChange={setSite} options={[{ value: "", label: "Site: All" }, ...sites]} placeholder="Site: All" />
              </Mui.Box>
              <Mui.Box sx={{ flex: 1 }} />
              <Mui.TextField size="small" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search employee" sx={{ ...globalTextFieldSx, width: { xs: "100%", md: 240 } }} />
              <SecondaryButton
                startIcon={<FileDownloadOutlined />}
                onClick={() => downloadRunWorkbook({ fileName: `bonus_${draftType?.name || "run"}_${draft.from_month}_${draft.to_month}.xlsx`.replace(/\s+/g, "_"), rows: draft.rows, type: draftType, from: draft.from_month, to: draft.to_month })}
              >
                Download
              </SecondaryButton>
            </Mui.Stack>
            {view === "summary" ? (
              <SummaryTable rows={rows} type={draftType} onRowClick={(row) => setEmployeeId(row.employee_id)} selectedId={employeeId} />
            ) : (
              <MonthwiseTable rows={rows.filter((row) => row.result === "eligible")} allEligible={allEligible} from={draft.from_month} to={draft.to_month} />
            )}
            <Mui.Typography sx={{ ...designType.captionText, mt: 1.5 }}>
              {view === "summary"
                ? "Earlier Bonus = bonus already added for the same type and months in earlier runs — paid in salary or waiting in payroll. Click an employee for the breakdown and each month."
                : `Each cell: bonus for the month${draftType?.method === "percent_of_wages" ? ` (${Number(draftType.rate)}% of wages used)` : ""} and, below it, wages used. Bottom row = all eligible employees, for reconciling with each month's payroll. Rounded to the rupee on the total only.`}
            </Mui.Typography>
          </Mui.Box>
          <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end" alignItems="center" sx={{ mt: 2 }}>
            {!payable.length ? <Mui.Typography sx={designType.captionText}>Nobody has anything to pay — nothing to add to payroll.</Mui.Typography> : null}
            <SecondaryButton startIcon={<ArrowBack />} onClick={() => { setError(null); setStep(0); }}>Back</SecondaryButton>
            <PrimaryButton endIcon={<ArrowForward />} disabled={stale || busy || !payable.length} onClick={() => { setError(null); setStep(2); }}>Next: Add to payroll</PrimaryButton>
          </Mui.Stack>
          {selectedRow ? <EmployeeMonthsPanel row={selectedRow} type={draftType} from={draft.from_month} to={draft.to_month} onClose={() => setEmployeeId(null)} /> : null}
        </>
      ) : null}

      {step === 2 && draft ? (
        <>
          <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.2fr) minmax(0, 1fr)" }, gap: 2, alignItems: "start" }}>
            <SectionCard title="Review">
              <DetailRows
                rows={[
                  { label: "Bonus type", value: draftType?.name },
                  { label: "Months", value: `${periodText(draft.from_month, draft.to_month)} (${monthsBetween(draft.from_month, draft.to_month).length})` },
                  { label: "Paying", value: draft.pay_mode === "part" ? `Part · ${draft.part_percent}% of the full bonus due, never more than the balance` : "Full bonus due, minus earlier bonus" },
                  { label: "Pay with", value: cycleDates(cycle) },
                  { label: "Bonus type version", value: `v${draft.type_version}` },
                  { label: "Employees paid", value: payable.length },
                  { label: "Left out", value: `${leftOut} (${draft.totals.excluded} excluded · ${draft.totals.check} need checking)${nothingToPay ? ` · ${nothingToPay} eligible with nothing to pay` : ""}` },
                  { label: "Bonus due", value: rupees(payable.reduce((s, r) => s + r.due, 0)) },
                  { label: "Earlier bonus", value: rupees(earlierPaid + earlierWaiting) },
                  { label: "Paid in salary", value: rupees(earlierPaid), indent: true },
                  { label: "Waiting in payroll", value: rupees(earlierWaiting), indent: true },
                  { label: "Total paying now", value: rupees(payable.reduce((s, r) => s + r.pay_now, 0)), color: "#067647", big: true },
                ]}
              />
              <Mui.Box sx={{ mt: 2 }}>
                <Field label="Name this run (optional)" wide>
                  <TextField value={runName} onChange={(value) => setRunName(value.slice(0, 100))} placeholder={`e.g. ${forPeriod(draft.from_month, draft.to_month)} bonus`} />
                </Field>
              </Mui.Box>
            </SectionCard>
            <SectionCard title="What happens when you click Add to payroll" tone="teal">
              <Mui.Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.5, ...designType.tableBodyCell }}>
                <li>{payable.length} bonus entries are created as <b>Ready</b> in {cycle?.name} payroll</li>
                <li>Each entry keeps its month-wise calculation, so payroll pays this amount and never re-calculates it</li>
                <li>You can cancel the whole run until {cycle?.name} payroll is run</li>
                <li>The month-wise sheet can be downloaded from the run at any time</li>
                {leftOut ? <li>The {leftOut} left out can be added later in a new run</li> : null}
              </Mui.Stack>
            </SectionCard>
          </Mui.Box>
          <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 2 }}>
            <SecondaryButton startIcon={<ArrowBack />} disabled={busy} onClick={() => { setError(null); setStep(1); }}>Back</SecondaryButton>
            <PrimaryButton disabled={busy || stale} onClick={addToPayroll}>{busy ? "Adding..." : "Add to payroll"}</PrimaryButton>
          </Mui.Stack>
        </>
      ) : null}
    </Mui.Box>
  );
};
